import React, { useState } from 'react';
import Button from '~/modules/components/global/button';
import Modal from '~/modules/components/global/modal';
import usePasskeys from '~/modules/hooks/use-passkeys';
import { ProfilePreferencesSmallLegend } from '~/modules/components/global/preferences/preferences.css';
import {
	Actions,
	ErrorText,
	ManualEntry,
	MethodAction,
	MethodControls,
	MethodInfo,
	MethodPanel,
	MethodRow,
	SetupStep,
	StateChip
} from '~/modules/components/global/preferences/two-factor-app.css';

/**
 * What a passkey is, in the terms that matter when deciding whether two of them are really two.
 *
 * A synced credential lives in iCloud Keychain or Google Password Manager and survives losing
 * the device; a device-bound one -- Windows Hello, a security key -- does not. The provider name
 * comes from the authenticator's AAGUID, and is absent for the many that decline to say.
 */
const describe = (passkey) => {
	const where = passkey.synced ? 'synced' : 'this device only';

	return passkey.provider ? `${passkey.provider} · ${where}` : where;
};

const when = (seconds) => {
	if(!seconds) {
		return null;
	}

	return new Date(seconds * 1000).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	});
};

/**
 * Passkeys, in profile preferences.
 *
 * Its own section rather than a row inside two-factor, because a passkey is not a second factor:
 * it stands in for the password entirely. Whoever holds the authenticator has proved possession
 * and been verified by it in one step, which is why a passkey sign-in is not asked for a code
 * afterwards.
 *
 * Nothing here is shown once and never again — no secret to transcribe, no recovery codes to
 * save — because the private key never leaves the device. That makes this the simplest of the
 * three sign-in methods to set up and the hardest to phish.
 */
const PasskeysSection = () => {
	const {
		supported, configured, confirmWith, passkeys, isLoading,
		error, setError, add, isAdding, remove, isBusy
	} = usePasskeys();

	const [adding, setAdding] = useState(false);
	const [password, setPassword] = useState('');
	const [name, setName] = useState('');
	// The one just made, when it left the account without a backup. Keeping it here rather than
	// closing the dialog is the whole point: the moment somebody has proved they can make a
	// passkey is the moment they can make the second one, and any other moment is one they have
	// to be persuaded back to.
	const [needsBackup, setNeedsBackup] = useState(null);

	// Nothing to offer when the browser cannot do WebAuthn, or the deployment has no relying
	// party: both mean a button that could only fail.
	if(!supported || isLoading || !configured) {
		return null;
	}

	const close = () => {
		setAdding(false);
		setPassword('');
		setName('');
		setNeedsBackup(null);
		setError(null);
	};

	const submit = async(e, on) => {
		e.preventDefault();

		// The backup is asked for the moment the first passkey exists, before the list has
		// refetched -- so `confirmWith` may still say `none` from before it was made. The first
		// ceremony spent the enrollment grant, and the API asks the second to prove itself with
		// the passkey it now holds; trusting the stale answer skipped that proof and the second
		// registration was refused. When there is a backup to add, there is a passkey to confirm
		// with, whatever the list last said.
		const proof = needsBackup ? 'passkey' : confirmWith;

		const created = await add({ password, name: name.trim(), confirmWith: proof, on })
			.catch(() => null);

		setPassword('');

		if(!created) {
			return;
		}

		// A device-bound credential -- Windows Hello, a security key -- dies with the machine it
		// was made on, so the account is one lost laptop from an administrator reset. A synced
		// one is its own backup. Either way this only comes up while there is nothing else on
		// the account to fall back to.
		if(!created.synced && passkeys.length === 0) {
			setName('');
			setNeedsBackup(created);

			return;
		}

		close();
	};

	return (
		<fieldset>
			<legend className="h5" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
				Passkeys
				<StateChip on={passkeys.length > 0} data-testid="passkeys-state">
					{passkeys.length > 0 ? 'On' : 'Off'}
				</StateChip>
			</legend>

			{passkeys.length
				? (
					<MethodPanel>
						{passkeys.map((passkey) => (
							<MethodRow key={passkey.id}>
								<MethodInfo>
									<strong>{passkey.name}</strong>
									<span>
										{describe(passkey)}
										{' · '}
										{passkey.lastUsedAt
											? `Last used ${when(passkey.lastUsedAt)}`
											: `Added ${when(passkey.createdAt) || 'recently'}`}
									</span>
								</MethodInfo>
								<MethodControls>
									{/* The state column, left empty: a passkey in the list is
									    self-evidently on, so the chip said nothing. The cell
									    stays so Remove lines up with the controls in the
									    second-factor rows below. */}
									<span aria-hidden="true"/>
									<MethodAction>
										<Button
											variant="ghost-primary"
											size="sm"
											type="button"
											data-testid={`passkey-remove-${passkey.id}`}
											disabled={isBusy}
											onClick={() => remove({ id: passkey.id })
												.catch(() => { /* surfaced through `error` */ })}
										>
											Remove
										</Button>
									</MethodAction>
								</MethodControls>
							</MethodRow>
						))}
					</MethodPanel>
				)
				: null}

			<Actions>
				<Button
					variant={passkeys.length ? 'secondary' : 'primary'}
					size="sm"
					type="button"
					data-testid="passkey-add"
					disabled={isBusy}
					onClick={() => {
						close();
						setAdding(true);
					}}
				>
					{passkeys.length ? 'Add another' : 'Add a passkey'}
				</Button>
			</Actions>

			<ProfilePreferencesSmallLegend style={{ padding: '0.5rem 0 0' }}>
				Sign in with your fingerprint, face or device PIN. A passkey cannot be typed into
				the wrong site, so it cannot be phished.
			</ProfilePreferencesSmallLegend>

			{error && !adding
				? <ErrorText data-testid="passkeys-error">{error}</ErrorText>
				: null}

			<Modal
				isOpen={adding}
				size={480}
				shouldCloseOnOverlayClick={!isAdding}
				onRequestClose={isAdding ? undefined : close}
			>
				<Modal.Header>
					<h3>{needsBackup ? 'Add a backup' : 'Add a passkey'}</h3>
				</Modal.Header>
				<Modal.Body>
					{ needsBackup
						? (
							<SetupStep>
								<p>
									<strong>{needsBackup.name || 'That passkey'}</strong> lives on
									this device only — if you lose it, you lose the account with
									it. Add a second one somewhere else before you finish.
								</p>
								<p>
									A security key, or a phone or tablet. Somewhere that is not
									signed into the same account as this machine, or you will end
									up with one passkey in two places rather than two passkeys.
								</p>

								<ManualEntry>
									<label htmlFor="px-passkey-backup-name">
										Name it (optional)
									</label>
									<input
										id="px-passkey-backup-name"
										data-testid="passkey-backup-name-input"
										type="text"
										placeholder="Security key"
										value={name}
										autoFocus
										onChange={(e) => setName(e.target.value)}
									/>
								</ManualEntry>

								<Actions>
									<Button
										variant="primary"
										size="sm"
										type="button"
										data-testid="passkey-backup-add"
										loading={isAdding}
										disabled={isBusy}
										onClick={(e) => submit(e, 'phone')}
									>
										Add a backup
									</Button>
									<Button
										variant="ghost-primary"
										size="sm"
										type="button"
										data-testid="passkey-backup-skip"
										disabled={isAdding}
										onClick={close}
									>
										Not now
									</Button>
								</Actions>

								{error
									? <ErrorText data-testid="passkeys-error">{error}</ErrorText>
									: null}
							</SetupStep>
						)
						: null }

					<form onSubmit={submit} hidden={!!needsBackup}>
						<SetupStep>
							<p>
								{confirmWith === 'passkey'
									? `Your device will ask you twice: once for a passkey you
										already have, and once to make the new one.`
									: 'Your device will ask you to confirm.'}
								{' '}
								Choose <strong>Add on a phone</strong> to scan a QR code and keep
								the new passkey there instead. Nothing is stored here that could
								be stolen and replayed — the key never leaves the device that
								makes it.
							</p>

							{confirmWith === 'password'
								? (
									<ManualEntry>
										<label htmlFor="px-passkey-password">
											Confirm your password
										</label>
										<input
											id="px-passkey-password"
											data-testid="passkey-password-input"
											type="password"
											autoComplete="current-password"
											value={password}
											autoFocus
											onChange={(e) => setPassword(e.target.value)}
										/>
									</ManualEntry>
								)
								: null}

							<ManualEntry>
								<label htmlFor="px-passkey-name">Name this device (optional)</label>
								<input
									id="px-passkey-name"
									data-testid="passkey-name-input"
									type="text"
									placeholder="Work laptop"
									value={name}
									autoFocus={confirmWith !== 'password'}
									onChange={(e) => setName(e.target.value)}
								/>
							</ManualEntry>

							<Actions>
								<Button
									variant="primary"
									size="sm"
									type="submit"
									loading={isAdding}
									disabled={isBusy || (confirmWith === 'password' && !password)}
								>
									This device
								</Button>
								{/* Asks the browser for a cross-platform authenticator, which is
								    what makes it show a QR code to scan rather than reaching for
								    the fingerprint reader on this machine -- the one that
								    already holds a passkey and would refuse to make a second. */}
								<Button
									variant="secondary"
									size="sm"
									type="button"
									data-testid="passkey-add-phone"
									disabled={isBusy || (confirmWith === 'password' && !password)}
									onClick={(e) => submit(e, 'phone')}
								>
									Add on a phone
								</Button>
								<Button
									variant="ghost-primary"
									size="sm"
									type="button"
									disabled={isAdding}
									onClick={close}
								>
									Cancel
								</Button>
							</Actions>

							{error
								? <ErrorText data-testid="passkeys-error">{error}</ErrorText>
								: null}
						</SetupStep>
					</form>
				</Modal.Body>
			</Modal>
		</fieldset>
	);
};

export default PasskeysSection;
